/**
 * Pure pixel-geometry pass for the swimlane process views (flow / sequence) —
 * the DOM-free replacement for SwimlaneView's browser measurement loop.
 *
 * The live view renders blocks as HTML divs in a CSS grid and reads row/block
 * geometry back from the DOM (`getBoundingClientRect` + `ResizeObserver`).
 * This module reproduces that sizing in pure math: text wrap via the scene
 * `wrapText` + a `Measure`, explicit min-height floors, and the same per-row
 * padding model (`framePadding`) the live overlay draws against. Ported from
 * the **committed** `SwimlaneView.tsx`
 * (`git show HEAD:packages/app-shell/src/process/SwimlaneView.tsx`) —
 * committed quirks are preserved, not improved (see GATEWAY_ROW_H's note in
 * swimlane-metrics.ts for one the browser itself neutralises).
 *
 * All output y/x are CANVAS coordinates: the lane-header band
 * (`LANE_HEADER_H`) is already included, matching the live overlay's
 * `blockYBounds` convention.
 *
 * Row classification that the live view derives from the walk (comment rows,
 * block labels) enters via `SwimlaneGeometryOptions` resolvers; the defaults
 * mirror the committed view's walk-less fallbacks (`#N` step labels, no
 * comment rows, fresh-open branch selection).
 */

import type { FontSpec } from "../model.js";
import { wrapText, type Measure } from "../text.js";
import type { Frame, Lane, Row, SwimlaneSceneInput, SwimlaneVariant } from "./types.js";
import {
  ARROW_BAND,
  ARROW_BAND_MAX,
  BLOCK_BORDER,
  BLOCK_INSET,
  BLOCK_LINE_H,
  BLOCK_MIN_H,
  BLOCK_PAD_Y,
  BLOCK_SUB_LINE_H,
  BLOCK_SUB_MARGIN_TOP,
  BLOCK_SUB_FONT_SIZE,
  BLOCK_TAG_MARGIN_BOTTOM,
  BLOCK_FONT_SIZE,
  BLOCK_W,
  BLOCK_WRAP_W,
  BOARD_RIGHT_PAD,
  CANVAS_BOTTOM_PAD,
  COMMENT_BAND,
  DO_CARET_GAP,
  DO_CARET_W,
  DO_FLOW_MIN_H,
  DO_FLOW_PAD_X,
  DO_FLOW_PAD_Y,
  DO_SEQ_BAND_H,
  DO_SEQ_INSET,
  EACH_BADGE_BAND,
  FRAME_EDGE_INSET,
  FRAME_GAP,
  FRAME_TITLE_BAND,
  LABEL_BAND,
  LANE_HEADER_H,
  LANE_WIDTH,
  NESTED_INSET_X,
  NESTED_INSET_Y,
  ROW_BASE_PAD,
  ROW_MIN_H,
  SEQ_FRAME_BOTTOM_PAD,
  SEQ_FRAME_TOP_PAD,
  TERM_CHIP_BORDER,
  TERM_CHIP_FONT_SIZE,
  TERM_CHIP_GAP,
  TERM_CHIP_PAD_X,
  TERM_H,
  TERM_MIN_W,
  TERM_PAD_X,
  TERM_FONT_SIZE,
  TRIGGER_MARKER_W,
  swimlaneMetrics,
  type SwimlaneMetrics,
  type SwimlaneScale,
} from "./swimlane-metrics.js";
import { defaultProcessTokens } from "./tokens.js";

// ─────────────────────────────────────────────────────────────────────────────
// Options & output types
// ─────────────────────────────────────────────────────────────────────────────

export interface SwimlaneBlockLabel {
  /** Main text, wrapped at the block width. */
  readonly primary: string;
  /** Secondary muted line (flow: `from <Module>`). */
  readonly subtitle?: string;
}

export interface SwimlaneGeometryOptions extends SwimlaneScale {
  /**
   * Per-row block content. The live view derives this from the walk
   * (`labelForStep`); headless callers thread the same data through here.
   * Default mirrors the committed walk-less fallback: `#<stepIndex+1>`.
   * Only consulted for step rows (terminator / subprocess rows label
   * themselves from the Row).
   */
  readonly blockLabel?: (row: Row, rowIndex: number) => SwimlaneBlockLabel;
  /**
   * True for a step row that is an ownerless prose/note row (walk-derived
   * in the live view: `noteText` + no caller). Default: never — the
   * committed view's behavior when no walk is supplied.
   */
  readonly isCommentRow?: (row: Row, rowIndex: number) => boolean;
  /** Explicit per-frame branch picks (live: `walkerState.branchSelection`).
   *  Default: empty — the fresh-open state. */
  readonly branchSelection?: ReadonlyMap<string, string>;
  /**
   * Row indexes that are START triggers (a leading inbound `await`) — walk-
   * derived in the live view from `walk.triggers` (SwimlaneView `isTrigger`).
   * A trigger STEP block renders a `▶` marker inline before line 0, so its
   * first line wraps `TRIGGER_MARKER_W` narrower (the composer draws the same
   * glyph). Default: none. Comment / prose rows carry no visible marker, so the
   * budget applies to step blocks only. Also consumed by `composeSwimlane` to
   * paint the `▶` and stamp `data-trigger`.
   */
  readonly triggerRowIndexes?: ReadonlySet<number>;
  /**
   * Row indexes that are MID-FLOW `await` waits (an `await` step that is not a
   * leading start trigger — PROCESS.md §6). Such a step block renders a timer
   * (clock) marker inline before line 0, so its first line wraps
   * `TRIGGER_MARKER_W` narrower — the same budget the `▶` trigger marker uses
   * (both are ~9px glyphs). Default: none. Also consumed by `composeSwimlane` to
   * paint the clock and stamp `data-wait`. Distinguishes a wait from a plain note.
   */
  readonly waitRowIndexes?: ReadonlySet<number>;
  /** Font family for all text (default: the process tokens' stack). */
  readonly fontFamily?: string;
}

export interface SwimlaneRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface SwimlaneAnchor {
  readonly x: number;
  readonly y: number;
}

export type SwimlaneBlockKind = "step" | "comment" | "prose" | "do" | "terminator";

export interface SwimlaneBlockGeom {
  /** Index into `input.rows`. */
  readonly rowIndex: number;
  readonly y: number;
  /** Lane column; −1 for an unanchored full-width prose row (and mirrored
   *  for the committed terminator quirk of registering lane −1). */
  readonly laneIndex: number;
  readonly kind: SwimlaneBlockKind;
  /** The block's visual box (canvas coords). */
  readonly rect: SwimlaneRect;
  readonly cx: number;
  readonly cy: number;
  /** Wrapped primary text lines. */
  readonly lines: readonly string[];
  /** Wrapped subtitle lines (empty when none). */
  readonly subtitleLines: readonly string[];
  readonly font: FontSpec;
  readonly subFont: FontSpec;
  /** Branch tag line shown above the label (only on dimmed alternatives). */
  readonly branchTag?: string;
  /** Unselected branch alternative — live renders at reduced opacity. */
  readonly dimmed: boolean;
  /** Arrow attachment points on the visual box edges. */
  readonly anchors: {
    readonly left: SwimlaneAnchor;
    readonly right: SwimlaneAnchor;
    readonly top: SwimlaneAnchor;
    readonly bottom: SwimlaneAnchor;
  };
}

export interface SwimlaneLaneGeom {
  readonly id: string;
  readonly kind: Lane["kind"];
  readonly label: string;
  /** {@link label} wrapped to the header chip's text budget, capped at
   *  `LANE_HEADER_MAX_LINES` (the last line ellipsized when it overflows).
   *  Both backends render THESE lines — a long underscore-joined module name
   *  must not spill out of the chip. The full name stays on {@link label}. */
  readonly labelLines: readonly string[];
  readonly index: number;
  readonly x: number;
  readonly width: number;
  /** Lane centre — lifeline / gateway x reference. */
  readonly cx: number;
}

export interface SwimlaneFrameBranchGeom {
  readonly label: string;
  readonly selected: boolean;
  /** Canvas y of the branch band's first row top (divider/label line). */
  readonly topY: number;
  /** Band divider carries its own label headroom (bands after the first). */
  readonly labelled: boolean;
}

export interface SwimlaneFrameGeom {
  readonly id: string;
  readonly kind: Frame["kind"];
  readonly label: string;
  readonly collapsed: boolean;
  readonly depth: number;
  readonly rect: SwimlaneRect;
  readonly branches: readonly SwimlaneFrameBranchGeom[];
  readonly mergeAspect?: string;
  readonly runCount?: number;
}

export interface SwimlaneGatewayGeom {
  readonly frameId: string;
  readonly frameKind: Frame["kind"];
  readonly splitX: number;
  readonly splitCy: number;
  readonly mergeX: number;
  readonly mergeCy: number;
  /** `parallel out` — no merge diamond (branches don't rejoin). */
  readonly detached: boolean;
  /** False for parallel (auto branch labels are suppressed). */
  readonly labelled: boolean;
}

export interface SwimlaneGeometry {
  readonly variant: SwimlaneVariant;
  readonly lanes: readonly SwimlaneLaneGeom[];
  /** Lane-header band height (rows start below it). */
  readonly headerH: number;
  /** Drawing width (lanes only) / container width (incl. right pad). */
  readonly width: number;
  readonly containerWidth: number;
  /** Total canvas height (header + grid + bottom pad). */
  readonly height: number;
  /** Canvas y of each layout row's top / its height (indexed by row `y`). */
  readonly rowTops: readonly number[];
  readonly rowHeights: readonly number[];
  /** Per-row cell padding the live overlay calls rowTopPad/rowBottomPad. */
  readonly rowTopPads: readonly number[];
  readonly rowBottomPads: readonly number[];
  /** Sum of row heights (the live RowGeometry's `total`). */
  readonly gridHeight: number;
  /** Visual block boxes keyed `${y}:${laneIndex}` (RowGeometry's key). */
  readonly blocks: ReadonlyMap<string, SwimlaneBlockGeom>;
  readonly blockList: readonly SwimlaneBlockGeom[];
  readonly frames: readonly SwimlaneFrameGeom[];
  /** Flow gateway diamonds (empty for sequence). */
  readonly gateways: readonly SwimlaneGatewayGeom[];
  /** The scale-resolved metric set this geometry was computed with — the live
   *  view and composer read font sizes / line-heights / insets from here so
   *  every renderer paints at the SAME `fontScale`/`layoutScale`. */
  readonly metrics: SwimlaneMetrics;
  /** Row indices hidden by an explicit branch pick. */
  readonly hiddenRowIndexes: ReadonlySet<number>;
  /** Layout `y`s that hold only a gateway diamond. */
  readonly gatewayRowYs: ReadonlySet<number>;
}

/** RowGeometry's block key: `${row.y}:${laneIndex}`. */
export function swimlaneBlockKey(y: number, laneIndex: number): string {
  return `${y}:${laneIndex}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared row predicates (ported from SwimlaneView / frameAffordance)
// ─────────────────────────────────────────────────────────────────────────────

/** if / try — exactly one branch runs, so a pick may hide the others. */
export function isFrameExclusive(kind: Frame["kind"]): boolean {
  return kind === "if" || kind === "try";
}

/** Every kind but `parallel` draws branch labels (parallel's are auto
 *  `branch:N` noise) — ported from frameAffordance.ts. */
export function showsBranchLabels(kind: Frame["kind"]): boolean {
  return kind !== "parallel";
}

/** Branch identity (`frameId:label`) stamped on a row by core, or null
 *  when the row is outside any if/switch/try branch. */
export function branchIdOfRow(row: Row): string | null {
  return row.branchFrameId && row.branchLabel
    ? `${row.branchFrameId}:${row.branchLabel}`
    : null;
}

/** A flow-chain SINK — a row that ends its forward path. A `go` (`reentry`)
 *  transfers control to its `as` target (PROCESS.md §7) and a terminator ends
 *  the branch (§10); neither falls through. Such a row still RECEIVES its
 *  incoming edge (it is a valid target), but is never selected as a following
 *  step's predecessor — so no spurious forward continuation is drawn out of it. */
export function isFlowSink(row: Row): boolean {
  return row.reentry === true || row.terminator != null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Topology — the flow chain + gateway model, shared by the sizing pass below
// and the swimlane composer. Ported from the committed SwimlaneView's
// `gateways`/`flowChain`/`gatewaySkip` memos (lines 300-560), including the
// fork/continuation anchors the overlay's arrows attach to.
// ─────────────────────────────────────────────────────────────────────────────

export interface SwimlaneTopologyOptions extends SwimlaneScale {
  /** See {@link SwimlaneGeometryOptions.isCommentRow}. */
  readonly isCommentRow?: (row: Row, rowIndex: number) => boolean;
  /** See {@link SwimlaneGeometryOptions.branchSelection}. */
  readonly branchSelection?: ReadonlyMap<string, string>;
}

export interface SwimlaneGwBranch {
  readonly label: string;
  /** Branch entry/exit node — a flow node, or a nested gateway's split (head)
   *  / merge (tail) row. */
  readonly head: Row;
  readonly tail: Row;
  readonly selected: boolean;
  /** try-catch arm (any non-first branch of a `try`). */
  readonly isCatch: boolean;
}

/** Fork/continuation anchor: a flow-node block (`x` null — use lane centre +
 *  block edges) or another frame's gateway diamond (`x` = that diamond's x). */
export interface SwimlaneGwAnchor {
  readonly row: Row;
  readonly x: number | null;
}

export interface SwimlaneGateway {
  readonly frame: Frame;
  readonly split: Row;
  readonly merge: Row;
  readonly branches: readonly SwimlaneGwBranch[];
  readonly fork: SwimlaneGwAnchor | null;
  readonly continuation: SwimlaneGwAnchor | null;
  readonly splitX: number;
  readonly mergeX: number;
  /** Branches hidden by an explicit exclusive pick (>0 ⇒ collapsed). */
  readonly hiddenCount: number;
  /** `parallel out` — no merge diamond; branches dangle. */
  readonly detached: boolean;
}

export interface SwimlaneTopology {
  readonly laneIndexOf: (laneId: string) => number;
  /** Lane centre of a row's callee lane (0 for an unknown lane). */
  readonly laneCxOf: (row: Row) => number;
  readonly isFlowNodeAt: (row: Row, rowIndex: number) => boolean;
  readonly flowChain: {
    readonly nodes: readonly { readonly row: Row; readonly idx: number }[];
    readonly prevOf: ReadonlyMap<number, Row>;
  };
  readonly gateways: readonly SwimlaneGateway[];
  readonly gatewaySkip: ReadonlySet<Row>;
  readonly gatewayRowYs: ReadonlySet<number>;
  readonly decisionsByFrame: ReadonlyMap<
    string,
    { readonly frame: Frame; readonly branches: readonly { readonly row: Row; readonly selected: boolean }[] }
  >;
  readonly branchEntryRows: ReadonlySet<Row>;
  readonly hiddenRowIndexes: ReadonlySet<number>;
}

export function swimlaneTopology(
  input: SwimlaneSceneInput,
  opts: SwimlaneTopologyOptions = {},
): SwimlaneTopology {
  const { LANE_WIDTH } = swimlaneMetrics(opts);
  const variant = input.variant;
  const branchSel = opts.branchSelection ?? new Map<string, string>();

  const laneIdx = new Map<string, number>();
  input.lanes.forEach((l) => laneIdx.set(l.id, l.index));
  const laneIndexOf = (id: string): number => laneIdx.get(id) ?? -1;
  const laneCxOf = (r: Row): number => {
    const li = laneIndexOf(r.toLaneId);
    return li < 0 ? 0 : li * LANE_WIDTH + LANE_WIDTH / 2;
  };

  const isComment = (r: Row, i: number): boolean =>
    r.stepIndex !== null && (opts.isCommentRow?.(r, i) ?? false);
  /** A row that participates in the flow chain (steps, do-headers,
   *  terminators, anchored prose) — the pre-first-step sentinel stays out. */
  const isFlowNodeAt = (r: Row, i: number): boolean => {
    if (r.subprocessHeader || r.terminator) return true;
    if (r.stepIndex === null) return false;
    if (isComment(r, i)) return r.toLaneId !== "process:notes";
    return true;
  };

  // Hidden rows: an explicitly-picked EXCLUSIVE frame shows only its chosen
  // branch (the walker ignores picks for switch/parallel).
  const exclusiveFrames = new Set(
    input.frames.filter((f) => isFrameExclusive(f.kind)).map((f) => f.id),
  );
  const hiddenRowIndexes = new Set<number>();
  input.rows.forEach((r, i) => {
    const fid = r.branchFrameId;
    if (fid && exclusiveFrames.has(fid) && branchSel.has(fid) && r.branchLabel !== branchSel.get(fid)) {
      hiddenRowIndexes.add(i);
    }
  });

  // Temporal chain: each flow node links to its predecessor, skipping
  // hidden rows, compensation rows, and sibling branches.
  const flowChain = (() => {
    const nodes: { row: Row; idx: number }[] = [];
    if (variant === "flow") {
      input.rows.forEach((r, idx) => {
        if (isFlowNodeAt(r, idx) && !hiddenRowIndexes.has(idx) && !r.comp) nodes.push({ row: r, idx });
      });
    }
    const prevOf = new Map<number, Row>();
    for (let i = 0; i < nodes.length; i++) {
      const { row, idx } = nodes[i]!;
      const thisBranch = branchIdOfRow(row);
      const thisSelected = row.branchSelected !== false;
      for (let j = i - 1; j >= 0; j--) {
        const cand = nodes[j]!.row;
        // A `go`/terminator is a flow sink — it never falls through, so it is
        // never a following step's predecessor (no forward edge out of a jump).
        if (isFlowSink(cand)) continue;
        const candBranch = branchIdOfRow(cand);
        const candSelected = cand.branchSelected !== false;
        if (thisSelected) {
          if (!candSelected) continue;
          if (candBranch && candBranch !== thisBranch) continue;
        } else {
          if (candBranch !== thisBranch) continue;
        }
        prevOf.set(idx, cand);
        break;
      }
    }
    return { nodes, prevOf };
  })();

  // First flow node of every branch of each if/switch/try frame (flow) —
  // unselected heads reserve arrow headroom via the decision pass.
  const decisionsByFrame = (() => {
    const out = new Map<string, { frame: Frame; branches: { row: Row; selected: boolean }[] }>();
    if (variant !== "flow") return out;
    for (const f of input.frames) {
      if (f.kind !== "if" && f.kind !== "switch" && f.kind !== "try") continue;
      const branches: { row: Row; selected: boolean }[] = [];
      for (const b of f.branches) {
        if (b.yEnd < b.yStart) continue;
        let firstRow: Row | null = null;
        input.rows.forEach((r, i) => {
          if (!isFlowNodeAt(r, i)) return;
          if (r.branchFrameId !== f.id || r.branchLabel !== b.label) return;
          if (!firstRow || r.y < firstRow.y) firstRow = r;
        });
        if (firstRow) branches.push({ row: firstRow, selected: b.selected });
      }
      if (branches.length) out.set(f.id, { frame: f, branches });
    }
    return out;
  })();

  const branchEntryRows = new Set<Row>();
  for (const d of decisionsByFrame.values()) {
    for (const b of d.branches) if (!b.selected) branchEntryRows.add(b.row);
  }

  // Gateway model (flow): split/merge diamond rows, branch heads/tails, diamond
  // x, and the fork/continuation anchors — the committed two-pass port.
  const gateways = (() => {
    const out: SwimlaneGateway[] = [];
    if (variant !== "flow") return out;
    const splitBy = new Map<string, Row>();
    const mergeBy = new Map<string, Row>();
    for (const r of input.rows) {
      if (r.gateway?.kind === "split") splitBy.set(r.gateway.frameId, r);
      else if (r.gateway?.kind === "merge") mergeBy.set(r.gateway.frameId, r);
    }
    type Prelim = Omit<SwimlaneGateway, "fork" | "continuation">;
    // Pass 1 — geom-free per-frame data (split/merge rows, branches, diamond x).
    const prelim: Prelim[] = [];
    const byId = new Map<string, Prelim>();
    for (const f of input.frames) {
      const split = splitBy.get(f.id);
      const merge = mergeBy.get(f.id);
      if (!split || !merge) continue;
      const branches: SwimlaneGwBranch[] = [];
      f.branches.forEach((b, bi) => {
        if (b.yEnd < b.yStart) return;
        // head/tail = FIRST/LAST stamped row in EXECUTION order (layout.rows
        // is pushed in walk order) — a flow node OR a nested gateway diamond.
        let head: Row | null = null;
        let tail: Row | null = null;
        for (const r of input.rows) {
          if (r.branchFrameId !== f.id || r.branchLabel !== b.label) continue;
          if (!head) head = r;
          tail = r;
        }
        if (head && tail) {
          branches.push({ label: b.label, head, tail, selected: b.selected, isCatch: f.kind === "try" && bi > 0 });
        }
      });
      if (!branches.length) continue;
      // Explicitly-selected exclusive frame: the gateway draws to the chosen
      // branch alone; the split/merge triangles still render.
      const picked = isFrameExclusive(f.kind) ? branchSel.get(f.id) : undefined;
      const shown = picked ? branches.filter((b) => b.label === picked) : branches;
      const branchesForGw = shown.length ? shown : branches;
      // Diamond x: try-catch anchors over the protected (try) branch; others
      // centre between the leftmost/rightmost branch heads (split) / tails.
      const tryB = branchesForGw.find((b) => !b.isCatch) ?? branchesForGw[0]!;
      const headXs = branchesForGw.map((b) => laneCxOf(b.head));
      const tailXs = branchesForGw.map((b) => laneCxOf(b.tail));
      const splitX = f.kind === "try" ? laneCxOf(tryB.head) : (Math.min(...headXs) + Math.max(...headXs)) / 2;
      const mergeX = f.kind === "try" ? laneCxOf(tryB.tail) : (Math.min(...tailXs) + Math.max(...tailXs)) / 2;
      const detached = f.kind === "parallel" && (f.mergeAspect ?? "").split(" ")[0] === "out";
      const entry: Prelim = {
        frame: f, split, merge, branches: branchesForGw, splitX, mergeX,
        hiddenCount: branches.length - branchesForGw.length, detached,
      };
      prelim.push(entry);
      byId.set(f.id, entry);
    }
    // Pass 2 — fork / continuation by macro-sequence neighbour: the nearest
    // connectable row above the split (fork) / below the merge (continuation),
    // in the same enclosing branch. A sibling frame contributes its MERGE as a
    // fork anchor / its SPLIT as a continuation anchor. When this gateway IS
    // its enclosing branch's head/tail, the OUTER frame wires that boundary.
    const anchorOf = (r: Row): SwimlaneGwAnchor => {
      if (r.gateway?.kind === "merge") return { row: r, x: byId.get(r.gateway.frameId)?.mergeX ?? laneCxOf(r) };
      if (r.gateway?.kind === "split") return { row: r, x: byId.get(r.gateway.frameId)?.splitX ?? laneCxOf(r) };
      return { row: r, x: null };
    };
    for (const e of prelim) {
      const f = e.frame;
      const enclBranchFrame = e.split.branchFrameId;
      const enclBranchLabel = e.split.branchLabel;
      let branchFirst: Row | null = null;
      let branchLast: Row | null = null;
      if (enclBranchFrame !== undefined) {
        for (const r of input.rows) {
          if (r.branchFrameId !== enclBranchFrame || r.branchLabel !== enclBranchLabel) continue;
          if (!branchFirst) branchFirst = r;
          branchLast = r;
        }
      }
      const isBranchHead = branchFirst === e.split;
      const isBranchTail = branchLast === e.merge;
      let fork: SwimlaneGwAnchor | null = null;
      let forkKey: readonly [number, number] = [-Infinity, -Infinity];
      let cont: SwimlaneGwAnchor | null = null;
      let contKey: readonly [number, number] = [Infinity, Infinity];
      input.rows.forEach((r, i) => {
        if (r.enclosingFrames.includes(f.id)) return;
        if (enclBranchFrame !== undefined && r.branchFrameId === enclBranchFrame && r.branchLabel !== enclBranchLabel) return;
        const forkable = r.gateway?.kind === "merge" || isFlowNodeAt(r, i);
        const contable = r.gateway?.kind === "split" || isFlowNodeAt(r, i);
        if (!isBranchHead && r.y < e.split.y && forkable && (r.y > forkKey[0] || (r.y === forkKey[0] && i > forkKey[1]))) {
          forkKey = [r.y, i];
          fork = anchorOf(r);
        }
        if (!isBranchTail && r.y > e.merge.y && contable && (r.y < contKey[0] || (r.y === contKey[0] && i < contKey[1]))) {
          contKey = [r.y, i];
          cont = anchorOf(r);
        }
      });
      out.push({ ...e, fork, continuation: cont });
    }
    return out;
  })();

  // Branch heads + flow-node continuations are drawn by the gateway pass —
  // the regular flow-arrow pass skips them.
  const gatewaySkip = new Set<Row>();
  for (const g of gateways) {
    for (const b of g.branches) gatewaySkip.add(b.head);
    if (g.continuation && g.continuation.x === null) gatewaySkip.add(g.continuation.row);
  }

  const gatewayRowYs = new Set<number>();
  for (const g of gateways) {
    gatewayRowYs.add(g.split.y);
    gatewayRowYs.add(g.merge.y);
  }

  return {
    laneIndexOf,
    laneCxOf,
    isFlowNodeAt,
    flowChain,
    gateways,
    gatewaySkip,
    gatewayRowYs,
    decisionsByFrame,
    branchEntryRows,
    hiddenRowIndexes,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The pass
// ─────────────────────────────────────────────────────────────────────────────

export function swimlaneGeometry(
  input: SwimlaneSceneInput,
  measure: Measure,
  opts: SwimlaneGeometryOptions = {},
): SwimlaneGeometry {
  // Scale-aware metrics: every constant below is destructured from this set,
  // shadowing the module's scale-1 imports so `fontScale`/`layoutScale` flow
  // through the whole geometry pass. Attached to the output so the live view
  // and composer render at the SAME scale.
  const metrics = swimlaneMetrics(opts);
  const {
    ARROW_BAND, ARROW_BAND_MAX, BLOCK_BORDER, BLOCK_FONT_SIZE, BLOCK_INSET,
    BLOCK_LINE_H, BLOCK_MIN_H, BLOCK_PAD_Y, BLOCK_SUB_FONT_SIZE, BLOCK_SUB_LINE_H,
    BLOCK_SUB_MARGIN_TOP, BLOCK_TAG_MARGIN_BOTTOM, BLOCK_W, BLOCK_WRAP_W,
    BOARD_RIGHT_PAD, CANVAS_BOTTOM_PAD, COMMENT_BAND, DO_CARET_GAP, DO_CARET_W,
    DO_FLOW_MIN_H, DO_FLOW_PAD_X, DO_FLOW_PAD_Y, DO_SEQ_BAND_H, DO_SEQ_INSET,
    EACH_BADGE_BAND, FRAME_EDGE_INSET, FRAME_GAP, FRAME_TITLE_BAND, GATEWAY_ROW_H,
    LABEL_BAND, LANE_HEADER_H, LANE_WIDTH, NESTED_INSET_X, NESTED_INSET_Y,
    ROW_BASE_PAD, ROW_MIN_H, SEQ_FRAME_BOTTOM_PAD, SEQ_FRAME_TOP_PAD,
    TERM_CHIP_BORDER, TERM_CHIP_FONT_SIZE, TERM_CHIP_GAP, TERM_CHIP_PAD_X,
    TERM_FONT_SIZE, TERM_H, TERM_MIN_W, TERM_PAD_X, TRIGGER_MARKER_W,
    BLOCK_MAX_LINES, LANE_HEADER_FONT_SIZE, LANE_HEADER_MAX_LINES, LANE_HEADER_TEXT_W,
  } = metrics;
  const variant = input.variant;
  const nLanes = Math.max(input.lanes.length, 1);
  const nRows = Math.max(input.height, 1);
  const family = opts.fontFamily ?? defaultProcessTokens().fontFamily;
  const font: FontSpec = { family, size: BLOCK_FONT_SIZE, weight: 400 };
  const subFont: FontSpec = { family, size: BLOCK_SUB_FONT_SIZE, weight: 400 };
  const termFont: FontSpec = { family, size: TERM_FONT_SIZE, weight: 400 };

  // Chain / gateway / hidden-row topology — shared with the composer.
  const topo = swimlaneTopology(input, opts);
  const { flowChain, gateways, gatewaySkip, gatewayRowYs, decisionsByFrame, branchEntryRows, hiddenRowIndexes } = topo;
  const laneIndex = topo.laneIndexOf;

  const isComment = (r: Row, i: number): boolean =>
    r.stepIndex !== null && (opts.isCommentRow?.(r, i) ?? false);

  // Arrow bands: head/foot room per row from the chain topology.
  const arrowBands = (() => {
    const above = new Array<number>(nRows).fill(0);
    const below = new Array<number>(nRows).fill(0);
    if (variant !== "flow") return { above, below };
    const bump = (arr: number[], y: number) => {
      if (y >= 0 && y < arr.length) arr[y]!++;
    };
    for (const { row, idx } of flowChain.nodes) {
      if (branchEntryRows.has(row) || gatewaySkip.has(row)) continue;
      const prev = flowChain.prevOf.get(idx);
      if (!prev) continue;
      if (prev.y === row.y) {
        if (Math.abs(laneIndex(row.toLaneId) - laneIndex(prev.toLaneId)) >= 2) bump(above, row.y);
        continue;
      }
      bump(above, row.y);
      bump(below, prev.y);
    }
    for (const d of decisionsByFrame.values()) {
      for (const b of d.branches) {
        if (b.selected) continue;
        bump(above, b.row.y);
      }
    }
    return { above, below };
  })();

  // Per-row cell padding (the live framePadding memo).
  const topPads = new Array<number>(nRows).fill(ROW_BASE_PAD);
  const bottomPads = new Array<number>(nRows).fill(ROW_BASE_PAD);
  if (variant === "sequence") {
    const bandLabelRows = new Set<number>();
    for (const f of input.frames) {
      if (f.yStart < topPads.length) topPads[f.yStart]! += SEQ_FRAME_TOP_PAD;
      const yEnd = Math.max(f.yEnd, f.yStart - 1);
      if (yEnd >= 0 && yEnd < bottomPads.length) bottomPads[yEnd]! += SEQ_FRAME_BOTTOM_PAD;
      for (const b of f.branches) {
        if (b.yStart === f.yStart || b.yEnd < b.yStart) continue;
        if (b.yStart >= 0 && b.yStart < topPads.length && !bandLabelRows.has(b.yStart)) {
          bandLabelRows.add(b.yStart);
          topPads[b.yStart]! += FRAME_TITLE_BAND;
        }
      }
    }
  } else {
    for (let y = 0; y < nRows; y++) {
      topPads[y]! += Math.min(arrowBands.above[y]!, ARROW_BAND_MAX) * ARROW_BAND;
      bottomPads[y]! += Math.min(arrowBands.below[y]!, ARROW_BAND_MAX) * ARROW_BAND;
    }
    const labelledHeadRows = new Set<number>();
    for (const g of gateways) {
      if (g.frame.kind === "parallel") continue;
      for (const b of g.branches) {
        if (b.head.y < 0 || b.head.y >= nRows || labelledHeadRows.has(b.head.y)) continue;
        labelledHeadRows.add(b.head.y);
        topPads[b.head.y]! += LABEL_BAND;
      }
    }
    for (const f of input.frames) {
      if (f.kind === "each" && f.yStart >= 0 && f.yStart < nRows) topPads[f.yStart]! += EACH_BADGE_BAND;
    }
  }
  const bandedCommentRows = new Set<number>();
  for (const r of input.rows) {
    if (r.comment && r.y >= 0 && r.y < nRows && !bandedCommentRows.has(r.y)) {
      bandedCommentRows.add(r.y);
      topPads[r.y]! += COMMENT_BAND;
    }
  }

  // ── Cell content heights (the browser's implicit sizing, made explicit) ──

  const wrap = (s: string, w: number, f: FontSpec, firstW = w, maxLines = Infinity): string[] =>
    s ? wrapText(s, w, f, measure, maxLines, firstW) : [];

  interface Cell {
    readonly rowIndex: number;
    readonly row: Row;
    readonly laneIndex: number;
    readonly kind: SwimlaneBlockKind;
    readonly lines: readonly string[];
    readonly subtitleLines: readonly string[];
    readonly branchTag?: string;
    /** Visual box height (border-box). */
    readonly boxH: number;
    /** Explicit box width when not the standard lane block. */
    readonly boxW?: number;
    readonly font: FontSpec;
  }
  const cells: Cell[] = [];
  input.rows.forEach((r, i) => {
    if (hiddenRowIndexes.has(i)) return;
    if (r.stepIndex === null) {
      if (r.gateway) return; // diamond lives in the overlay; row reserves height only
      if (r.subprocessHeader) {
        const label = r.subprocessHeader.label || "do";
        if (variant === "flow") {
          const wrapW = BLOCK_W - 2 * BLOCK_BORDER - 2 * DO_FLOW_PAD_X - DO_CARET_W - DO_CARET_GAP;
          const lines = wrap(label, wrapW, font, wrapW, BLOCK_MAX_LINES);
          const boxH = Math.max(
            DO_FLOW_MIN_H,
            2 * BLOCK_BORDER + 2 * DO_FLOW_PAD_Y + Math.max(lines.length, 1) * BLOCK_LINE_H,
          );
          cells.push({ rowIndex: i, row: r, laneIndex: laneIndex(r.toLaneId), kind: "do", lines, subtitleLines: [], boxH, font });
        } else {
          // Sequence: full-width single-line band (probed height).
          cells.push({ rowIndex: i, row: r, laneIndex: -1, kind: "do", lines: [label], subtitleLines: [], boxH: DO_SEQ_BAND_H, font });
        }
        return;
      }
      if (r.terminator) {
        const reason = r.terminator.reason ?? "";
        const textW = reason ? measure.text(reason, termFont).width : 0;
        // `fail all`/`finish all` renders an in-pill "all" chip after the
        // reason (committed 2193-2201, gap-1.5) — the pill grows for it.
        const chipW = r.terminator.all
          ? TERM_CHIP_GAP +
            2 * (TERM_CHIP_PAD_X + TERM_CHIP_BORDER) +
            measure.text("ALL", { family, size: TERM_CHIP_FONT_SIZE, weight: 400 }).width
          : 0;
        const boxW = Math.max(TERM_MIN_W, 2 * TERM_PAD_X + textW + chipW);
        cells.push({
          rowIndex: i,
          row: r,
          laneIndex: laneIndex(r.toLaneId), // committed quirk: −1 keys stay −1
          kind: "terminator",
          lines: reason ? [reason] : [],
          subtitleLines: [],
          boxH: TERM_H,
          boxW,
          font: termFont,
        });
      }
      return;
    }
    const label: SwimlaneBlockLabel = opts.blockLabel?.(r, i) ?? { primary: `#${r.stepIndex + 1}` };
    if (isComment(r, i)) {
      const li = laneIndex(r.toLaneId);
      const anchored = li >= 0;
      const wrapW = anchored ? BLOCK_W : Math.floor((nLanes * LANE_WIDTH - 2 * 24) * 0.7);
      const lines = wrap(label.primary, wrapW, font);
      cells.push({
        rowIndex: i,
        row: r,
        laneIndex: anchored ? li : -1,
        kind: anchored ? "comment" : "prose",
        lines,
        subtitleLines: [],
        boxH: Math.max(lines.length, 1) * BLOCK_LINE_H,
        boxW: anchored ? BLOCK_W : wrapW,
        font,
      });
      return;
    }
    const li = laneIndex(r.toLaneId);
    if (li < 0) return; // committed: BlockCell renders nothing for an unknown lane
    const dimmed = r.branchSelected === false;
    const branchTag = dimmed ? r.branchLabel : undefined;
    // A START-trigger (`▶`) or mid-flow-await wait (clock) step draws a marker
    // inline before line 0 — budget its width off the FIRST line so the painted
    // marker+text stays inside the box (both markers are the same ~9px width).
    const hasMarker = opts.triggerRowIndexes?.has(i) || opts.waitRowIndexes?.has(i);
    const firstLineW = hasMarker ? BLOCK_WRAP_W - TRIGGER_MARKER_W : BLOCK_WRAP_W;
    // A step block is a fixed-width node in a lane grid: cap its lines so one
    // very long identifier ellipsizes instead of growing the row without bound.
    const lines = wrap(label.primary, BLOCK_WRAP_W, font, firstLineW, BLOCK_MAX_LINES);
    const subtitleLines = label.subtitle
      ? wrap(label.subtitle, BLOCK_WRAP_W, subFont, BLOCK_WRAP_W, BLOCK_MAX_LINES)
      : [];
    let contentH = Math.max(lines.length, 1) * BLOCK_LINE_H;
    if (branchTag) contentH += BLOCK_SUB_LINE_H + BLOCK_TAG_MARGIN_BOTTOM;
    if (subtitleLines.length) contentH += subtitleLines.length * BLOCK_SUB_LINE_H + BLOCK_SUB_MARGIN_TOP;
    const boxH = Math.max(BLOCK_MIN_H, 2 * BLOCK_BORDER + 2 * BLOCK_PAD_Y + contentH);
    cells.push({ rowIndex: i, row: r, laneIndex: li, kind: "step", lines, subtitleLines, branchTag, boxH, font });
  });

  // Row heights: max cell (pads included), floored by the grid template's
  // ROW_MIN_H — including gateway rows (see GATEWAY_ROW_H's inertness note).
  const rowHeights = new Array<number>(nRows).fill(ROW_MIN_H);
  for (const c of cells) {
    const y = c.row.y;
    if (y < 0 || y >= nRows) continue;
    const cellH = topPads[y]! + c.boxH + bottomPads[y]!;
    if (cellH > rowHeights[y]!) rowHeights[y] = cellH;
  }
  const rowTops = new Array<number>(nRows).fill(LANE_HEADER_H);
  for (let y = 1; y < nRows; y++) rowTops[y] = rowTops[y - 1]! + rowHeights[y - 1]!;
  const gridHeight = rowHeights.reduce((a, b) => a + b, 0);

  // ── Blocks (canvas rects + anchors) ───────────────────────────────────────

  const blocks = new Map<string, SwimlaneBlockGeom>();
  const blockList: SwimlaneBlockGeom[] = [];
  for (const c of cells) {
    const y = c.row.y;
    if (y < 0 || y >= nRows) continue;
    const rowTop = rowTops[y]!;
    const regionTop = rowTop + topPads[y]!;
    const regionH = rowHeights[y]! - topPads[y]! - bottomPads[y]!;
    const by = regionTop + (regionH - c.boxH) / 2; // centred like the flex cell
    let bx: number;
    let bw: number;
    if (c.kind === "do" && variant === "sequence") {
      bx = DO_SEQ_INSET;
      bw = nLanes * LANE_WIDTH - 2 * DO_SEQ_INSET;
    } else if (c.kind === "prose") {
      bw = c.boxW ?? BLOCK_W;
      bx = (nLanes * LANE_WIDTH - bw) / 2;
    } else if (c.kind === "terminator") {
      bw = c.boxW ?? TERM_MIN_W;
      const lane = Math.max(c.laneIndex, 0); // col fallback: lane 0
      bx = lane * LANE_WIDTH + BLOCK_INSET + (BLOCK_W - bw) / 2;
    } else {
      bw = c.boxW ?? BLOCK_W;
      // Committed quirk: an unknown-lane `do` renders in column 1 (lane 0)
      // while keeping its −1 block key.
      bx = Math.max(c.laneIndex, 0) * LANE_WIDTH + BLOCK_INSET;
    }
    const rect: SwimlaneRect = { x: bx, y: by, w: bw, h: c.boxH };
    const cy = by + c.boxH / 2;
    const geom: SwimlaneBlockGeom = {
      rowIndex: c.rowIndex,
      y,
      laneIndex: c.laneIndex,
      kind: c.kind,
      rect,
      cx: bx + bw / 2,
      cy,
      lines: c.lines,
      subtitleLines: c.subtitleLines,
      font: c.font,
      subFont,
      branchTag: c.branchTag,
      dimmed: c.row.branchSelected === false,
      anchors: {
        left: { x: bx, y: cy },
        right: { x: bx + bw, y: cy },
        top: { x: bx + bw / 2, y: by },
        bottom: { x: bx + bw / 2, y: by + c.boxH },
      },
    };
    blocks.set(swimlaneBlockKey(y, c.laneIndex), geom);
    blockList.push(geom);
  }

  // ── Frames ────────────────────────────────────────────────────────────────

  const effectiveYEnd = (f: Frame): number => Math.max(f.yEnd, f.yStart - 1);
  const depths = (() => {
    const out = new Map<string, number>();
    for (const f of input.frames) {
      let d = 0;
      for (const o of input.frames) {
        if (o.id === f.id) continue;
        const yContains = o.yStart <= f.yStart && effectiveYEnd(o) >= effectiveYEnd(f);
        const xContains = o.laneSpan.start <= f.laneSpan.start && o.laneSpan.end >= f.laneSpan.end;
        const strictlyOuter =
          o.yStart < f.yStart || o.yEnd > f.yEnd ||
          o.laneSpan.start < f.laneSpan.start || o.laneSpan.end > f.laneSpan.end;
        if (yContains && xContains && strictlyOuter) d++;
      }
      out.set(f.id, d);
    }
    return out;
  })();
  // Sequence: a frame whose first row starts an OUTER band drops below that
  // band's divider + label zone.
  const frameTopExtras = new Map<string, number>();
  if (variant === "sequence") {
    for (const f of input.frames) {
      for (const o of input.frames) {
        if (o.id === f.id) continue;
        if (o.branches.some((b) => b.yStart === f.yStart && b.yStart > o.yStart && b.yEnd >= b.yStart)) {
          frameTopExtras.set(f.id, FRAME_TITLE_BAND);
          break;
        }
      }
    }
  }
  const frames: SwimlaneFrameGeom[] = input.frames.map((f) => {
    const depth = depths.get(f.id) ?? 0;
    const insetX = depth * NESTED_INSET_X;
    const insetY = depth * NESTED_INSET_Y;
    const topExtra = frameTopExtras.get(f.id) ?? 0;
    const x = f.laneSpan.start * LANE_WIDTH + FRAME_EDGE_INSET + insetX;
    const w = (f.laneSpan.end - f.laneSpan.start + 1) * LANE_WIDTH - 2 * FRAME_EDGE_INSET - 2 * insetX;
    const yStart = f.yStart;
    const yEnd = effectiveYEnd(f);
    const yPx = (rowTops[yStart] ?? LANE_HEADER_H) + insetY + FRAME_GAP + topExtra;
    let spanH = 0;
    for (let yy = yStart; yy <= yEnd; yy++) spanH += rowHeights[yy] ?? ROW_MIN_H;
    if (yEnd < yStart) spanH = rowHeights[yStart] ?? ROW_MIN_H;
    const h = spanH - 2 * insetY - 2 * FRAME_GAP - topExtra;
    const branches: SwimlaneFrameBranchGeom[] = f.branches
      .filter((b) => b.yEnd >= b.yStart)
      .map((b) => ({
        label: b.label,
        selected: b.selected,
        topY: rowTops[b.yStart] ?? LANE_HEADER_H,
        labelled: b.yStart !== f.yStart,
      }));
    return {
      id: f.id,
      kind: f.kind,
      label: f.label,
      collapsed: f.collapsed,
      depth,
      rect: { x, y: yPx, w, h: Math.max(h, 1) },
      branches,
      mergeAspect: f.mergeAspect,
      runCount: f.runCount,
    };
  });

  // ── Gateway diamonds (flow) ───────────────────────────────────────────────

  // Diamond centre = padded-region midpoint of its row (the live
  // blockYBounds fallback for lane −1 — gateway rows hold no block).
  const regionCy = (y: number): number => {
    const top = (rowTops[y] ?? LANE_HEADER_H) + (topPads[y] ?? ROW_BASE_PAD);
    const bottom = (rowTops[y] ?? LANE_HEADER_H) + (rowHeights[y] ?? ROW_MIN_H) - (bottomPads[y] ?? ROW_BASE_PAD);
    return (top + bottom) / 2;
  };
  const gatewayGeoms: SwimlaneGatewayGeom[] = gateways.map((g) => ({
    frameId: g.frame.id,
    frameKind: g.frame.kind,
    splitX: g.splitX,
    splitCy: regionCy(g.split.y),
    mergeX: g.mergeX,
    mergeCy: regionCy(g.merge.y),
    detached: g.detached,
    labelled: g.frame.kind !== "parallel",
  }));

  // ── Lanes / totals ────────────────────────────────────────────────────────

  const laneHeaderFont: FontSpec = { family, size: LANE_HEADER_FONT_SIZE, weight: 400 };
  const laneGeoms: SwimlaneLaneGeom[] = input.lanes.map((l) => ({
    id: l.id,
    kind: l.kind,
    label: l.label,
    labelLines: wrapText(
      l.label,
      LANE_HEADER_TEXT_W,
      laneHeaderFont,
      measure,
      LANE_HEADER_MAX_LINES,
    ),
    index: l.index,
    x: l.index * LANE_WIDTH,
    width: LANE_WIDTH,
    cx: l.index * LANE_WIDTH + LANE_WIDTH / 2,
  }));
  const width = nLanes * LANE_WIDTH;
  const height = LANE_HEADER_H + gridHeight + CANVAS_BOTTOM_PAD;

  return {
    variant,
    lanes: laneGeoms,
    headerH: LANE_HEADER_H,
    width,
    containerWidth: width + BOARD_RIGHT_PAD,
    height,
    rowTops,
    rowHeights,
    rowTopPads: topPads,
    rowBottomPads: bottomPads,
    gridHeight,
    blocks,
    blockList,
    frames,
    gateways: gatewayGeoms,
    metrics,
    hiddenRowIndexes,
    gatewayRowYs,
  };
}
